import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { GamesFilter, GameV1 } from '@/api/games';
import type { CurrencyCode } from '@/helpers/currency';
import { DEMO_CURRENCY } from '@/helpers/variables';
import { useGetCommonStore } from '@/store/commonStore';
import { applyVolatilityPattern } from '../helpers/volatilityPattern';
import {
  type Currencies,
  fetchWageringHead,
  fetchWageringPopularity,
  mergeWageringGames,
  PREFETCH_PAGES,
  type WageringHeadKey,
  type WageringPopularityKey,
} from '../helpers/wagering';
import { useVolatilityPattern } from './useVolatilityPattern';

const useRequestContext = () => {
  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const pendingWallets = useCommonStore.use.pendingWallets();
  const reasonForUpdatingUser = useCommonStore.use.reasonForUpdatingUser();
  const wallets = useCommonStore.use.wallets();

  const isSessionResolved = reasonForUpdatingUser !== undefined;

  const currencies = useMemo<Currencies>(() => {
    const walletCurrencies = wallets.map(
      ({ currency }) => currency
    ) as CurrencyCode[];

    return walletCurrencies.length > 0 ? walletCurrencies : [DEMO_CURRENCY];
  }, [wallets]);

  return {
    currencies,
    isAuthenticated,
    shouldWaitForUser:
      pendingWallets ||
      (isAuthenticated && !wallets.length && !isSessionResolved),
    device: (isMobileDevice ? 'mobile' : 'desktop') as GamesFilter.Device,
  };
};

const NO_GAMES: GameV1[] = [];

const isSettled = (data: unknown, error: unknown) =>
  data !== undefined || error !== undefined;

interface IUseWageringGamesParams {
  pageSize: number;
  enabled?: boolean;
}

export const useWageringGames = ({
  pageSize,
  enabled = true,
}: IUseWageringGamesParams) => {
  const { device, currencies, isAuthenticated, shouldWaitForUser } =
    useRequestContext();

  const isReady = enabled && !shouldWaitForUser;

  const headKey: WageringHeadKey | null = isReady
    ? ['wagering-head', device, currencies, isAuthenticated]
    : null;

  const { data: head, error: headError } = useSWR(
    headKey,
    ([, keyDevice, keyCurrencies]) =>
      fetchWageringHead(keyDevice, keyCurrencies),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const [extraPages, setExtraPages] = useState(0);
  const visible = pageSize * (extraPages + 1);

  useEffect(() => setExtraPages(0), [enabled]);

  const getPopularityKey = useCallback(
    (
      pageIndex: number,
      previous: GamesFilter.Response | null
    ): WageringPopularityKey | null => {
      if (!isReady) return null;
      if (previous && !previous.pagination.nextPage) return null;

      return [
        'wagering-popularity',
        device,
        currencies,
        isAuthenticated,
        pageIndex + 1,
      ];
    },
    [isReady, device, currencies, isAuthenticated]
  );

  const {
    data: popularityPages,
    error: popularityError,
    size,
    setSize,
    isLoading: isPopularityLoading,
  } = useSWRInfinite<GamesFilter.Response>(
    getPopularityKey,
    ([, keyDevice, keyCurrencies, , keyPage]: WageringPopularityKey) =>
      fetchWageringPopularity(keyDevice, keyCurrencies, keyPage),
    {
      revalidateFirstPage: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      persistSize: false,
    }
  );

  const baseOrder = useMemo(
    () => mergeWageringGames(head, popularityPages),
    [head, popularityPages]
  );

  const { pattern, isResolved: isPatternResolved } =
    useVolatilityPattern(isReady);
  const ordered = useMemo(
    () => (pattern ? applyVolatilityPattern(baseOrder, pattern) : baseOrder),
    [baseOrder, pattern]
  );

  const isLoading =
    enabled &&
    (shouldWaitForUser ||
      !isSettled(head, headError) ||
      !isSettled(popularityPages, popularityError) ||
      !isPatternResolved);

  const merged = isLoading ? NO_GAMES : ordered;

  const lastPage = popularityPages?.at(-1);
  const hasMorePopularity = Boolean(lastPage?.pagination.nextPage);
  const isFetchingPage =
    isPopularityLoading ||
    (size > 0 && popularityPages && popularityPages[size - 1] === undefined);

  const loadMore = useCallback(() => {
    setExtraPages((n) => n + 1);

    const needed = pageSize * (extraPages + 2 + PREFETCH_PAGES);
    if (merged.length < needed && hasMorePopularity && !isFetchingPage) {
      setSize((prev) => prev + 1);
    }
  }, [
    pageSize,
    extraPages,
    merged.length,
    hasMorePopularity,
    isFetchingPage,
    setSize,
  ]);

  return {
    games: merged.slice(0, visible),
    allGames: merged,
    totalCount:
      lastPage?.pagination.totalCount ?? head?.pagination.totalCount ?? 0,
    loadMore,
    isLoading,
  };
};
